import { assert } from '@ember/debug';
import { concat, fn, hash } from '@ember/helper';
import { dependentKeyCompat } from '@ember/object/compat';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import Model from '@warp-drive/legacy/model';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { storageFor } from 'ember-local-storage';

import Table from 'umg-ember-backend/components/record-explorer/table';
import Toolbar from 'umg-ember-backend/components/record-explorer/toolbar';
import Pagination from 'umg-ember-backend/components/ui/pagination';
import Window from 'umg-ember-backend/components/ui/window';
import { query } from 'umg-ember-backend/data/builders/umg-rest';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';

export default class RecordExplorerComponent extends Component {
  // Services
  @service abilities;
  @service breadcrumb;
  @service flashMessages;
  @service filter;
  @service history;
  @service intl;
  @service recordCopy;
  @service recordRelationships;
  @service router;
  @service store;
  @storageFor('table-config') tableConfigs;

  // Defaults
  @storeOnRoute('recordType', { prefix: 'filter' }) page;
  @storeOnRoute('recordType', { prefix: 'filter' }) searchString = '';
  @tracked selectedRecords = [];
  @tracked records = [];
  defaultColumns = [];
  tableColumns = [];

  defaultFilters = [
    {
      key: 'cmsState',
      type: 'selectValueLabel',
      options: [1, 2, 3, 5, 6],
    },
  ];

  // Getter and setter
  @dependentKeyCompat
  get activeFilters() {
    let config = this.tableConfig;
    const queryParams = this.router.currentRoute.queryParams;

    return {
      ...config.filters,
      ...queryParams,
    };
  }

  get activeColumns() {
    return this.tableConfig.columns || [...this.defaultColumns];
  }

  get activeSort() {
    return this.tableConfig.sort ?? 'cmsModified';
  }

  get availableFilters() {
    return [...this.defaultFilters];
  }

  get recordsData() {
    if (Array.isArray(this.records.data)) {
      return this.records.data;
    }

    return this.records;
  }

  get recordType() {
    assert(
      '<RecordExplorer> requires @recordType because @records may be a collection without stable type metadata.',
      this.args.recordType,
    );

    return this.args.recordType;
  }

  get pageSize() {
    return this.tableConfig.pageSize || 30;
  }

  @storeOnRoute('recordType') _scrollPosition;
  get scrollPosition() {
    return this.args.scrollPosition ?? this._scrollPosition;
  }

  get tableConfig() {
    return this.tableConfigs.get(this.recordType) || {};
  }

  get visibleColumns() {
    return this.tableColumns.filter((column) => {
      if (column.type === 'row-actions') {
        return true;
      }

      return this.activeColumns.includes(column.property);
    });
  }

  // Actions
  addRecord = (defaults) => {
    const newRecord = this.store.createRecord(this.recordType, defaults);
    const route = this.breadcrumb.registerDetailRoute(newRecord);

    this.router.transitionTo(...route.transitionToParams);
  };

  deleteRecord = async (model) => {
    let ok = await this.history.confirmAction('delete', model);

    if (!ok) {
      return;
    }

    try {
      await this.history.deleteModel(model);

      // TODO: Unify hasMany and belongsTo
      this.selectedRecords = [];
    } catch (error) {
      await model.rollbackAttributes();
      await this.recordRelationships.rollback(model);

      this.flashMessages.serverError(error);
    }
  };

  duplicateRecord = async (model) => {
    let duplicatedRecord = await this.recordCopy.copy(model);

    this.loadRecord(duplicatedRecord);
  };

  loadRecord = (model) => {
    if (this.abilities.can('read', model)) {
      let route = this.breadcrumb.registerDetailRoute(model);
      this.router.transitionTo(...route.transitionToParams);
    }
  };

  setOrLoadRecords = () => {
    const filters = {};

    if (this.args.filters) {
      Object.entries(this.args.filters).forEach(([key, value]) => {
        if (value instanceof Model) {
          filters[key] = value.id;
        } else {
          filters[key] = value;
        }
      });
    }

    if (this.args.records) {
      this.records = this.args.records;
    } else {
      this.updateTableConfig('filters', filters);
    }
  };

  setFilter = (key, value) => {
    this.page = 1;

    this.updateTableConfig(key, value);
  };

  setScrollPosition = (scrollPosition) => {
    if (this.args.onBodyScroll) {
      this.args.onBodyScroll(scrollPosition);
    } else {
      this._scrollPosition = scrollPosition;
    }
  };

  search = (value) => {
    this.searchString = value;
    this.page = 1;

    this.loadRecords.perform();
  };

  selectRecord = (model) => {
    let selectedRecords = this.selectedRecords;

    if (this.args.relationType !== 'hasMany') {
      selectedRecords = [];
    }

    const index = selectedRecords.indexOf(model);
    if (index !== -1) {
      selectedRecords.splice(index, 1);
    } else {
      selectedRecords.push(model);
    }

    this.selectedRecords = selectedRecords;
  };

  setPage = (page) => {
    this.page = page;

    this.loadRecords.perform();
  };

  updateTableConfig = (key, value) => {
    if (value) {
      // Update localStorage
      // TODO: Do wee need it?
      if (!this.tableConfigs.get(this.recordType)) {
        this.tableConfigs.set(this.recordType, {});
      }

      let config = {
        ...this.tableConfig,
        [key]: value,
      };

      this.tableConfigs.set(this.recordType, config);
    }

    this.loadRecords.perform();
  };

  // Functions
  loadRecords = task({ restartable: true }, async () => {
    try {
      const queryParams = this.router.currentRoute.queryParams;
      const queryObject = this.filter.getQueryObject(
        this.recordType,
        queryParams,
      );

      const { content } = await this.store.request(
        query(this.recordType, queryObject),
      );

      this.records = content;
    } catch (e) {
      this.flashMessages.serverError(e);
    }
  });

  // Template
  <template>
    <Window
      @isBottombarVisible={{if this.recordsData.length true false}}
      @isLoading={{this.loadRecords.isRunning}}
      @isSeamless={{true}}
      @onBodyScroll={{this.setScrollPosition}}
      @scrollPosition={{this.scrollPosition}}
      {{didInsert this.setOrLoadRecords}}
      ...attributes
    >
      <:title>
        {{t (concat 'models.plurals.' this.recordType)}}
      </:title>

      <:toolbar>
        <Toolbar
          @activeColumns={{this.activeColumns}}
          @activeFilters={{this.activeFilters}}
          @availableFilters={{this.availableFilters}}
          @columns={{this.tableColumns}}
          @recordType={{this.recordType}}
          @onAddRecord={{this.addRecord}}
          @onAssignRelation={{@onAssignRelation}}
          @onSearch={{this.search}}
          @onSetFilter={{this.setFilter}}
          @parentRecordType={{@parentRecordType}}
          @searchString={{this.searchString}}
          @selectedRecords={{this.selectedRecords}}
        />
      </:toolbar>

      <:body>
        <Table
          @activeSort={{this.activeSort}}
          @columns={{this.visibleColumns}}
          @records={{this.recordsData}}
          @recordType={{this.recordType}}
          @recordActions={{hash
            edit=this.loadRecord
            duplicate=this.duplicateRecord
            delete=this.deleteRecord
          }}
          @selectedRecords={{this.selectedRecords}}
          @onAssignRelation={{@onAssignRelation}}
          @onUpdateSort={{fn this.setFilter 'sort'}}
          @onSelectRecord={{this.selectRecord}}
        />
      </:body>

      <:bottombar>
        <Pagination
          @pagination={{this.records.meta}}
          @setPage={{this.setPage}}
          @setPageLimit={{fn this.setFilter 'pageSize'}}
        />
      </:bottombar>
    </Window>
  </template>
}
